import React, { useState } from "react";
import { FileText, CornerUpLeft, Loader2, AlertCircle } from "@semo/icons";
import { cn, ChatBubble, Typography } from "@semo/shared-ui";
import { FilePreviewModal } from "../atoms/FilePreviewModal";
import { MessageLayout } from "../layout/MessageLayout";
import { useChatActions } from "../../context/ChatActionContext";
import type { ChatMessageProps } from "../types";
import type {
  TextMessageResponseDto,
  AttachmentMessageResponseDto,
} from "@semo/api-contracts";
import type { LocalMessageState } from "../../types/message";

interface TextMessageProps extends Omit<ChatMessageProps, "message"> {
  message: (TextMessageResponseDto | AttachmentMessageResponseDto) &
    LocalMessageState;
  sender: "me" | "other";
  time: string;
}

export const TextMessage: React.FC<TextMessageProps> = ({
  message,
  sender,
  partyName,
  time,
  showAvatar = true,
  showName = true,
  isFirstInGroup = true,
  isLastInGroup = true,
}) => {
  const { id, isRead, isDeleted, messageType, isPending, isError } = message;
  const { onDownloadFile, onRetry, onImageLoad } = useChatActions();
  const [showPreview, setShowPreview] = useState(false);

  // Border Radius Logic for Grouping
  const getBorderRadiusClass = () => {
    // Only apply grouping styles if it's NOT a deleted message (system style)
    if (isDeleted) return "";

    if (sender === "me") {
      if (isFirstInGroup && !isLastInGroup) return "rounded-br-[2px]";
      if (!isFirstInGroup && !isLastInGroup) return "rounded-r-[2px]";
      if (!isFirstInGroup && isLastInGroup) return "rounded-tr-[2px]";
    } else {
      if (isFirstInGroup && !isLastInGroup) return "rounded-bl-[2px]";
      if (!isFirstInGroup && !isLastInGroup) return "rounded-l-[2px]";
      if (!isFirstInGroup && isLastInGroup) return "rounded-tl-[2px]";
    }
    return "";
  };

  const borderRadiusClass = getBorderRadiusClass();

  // Type narrowing for attachment
  const attachment =
    messageType === "attachment"
      ? (message as AttachmentMessageResponseDto).attachment
      : undefined;
  const isCmarketDocument =
    attachment?.description === "c-market에서 자동 생성된 계약서류";
  const text =
    messageType === "text" ? (message as TextMessageResponseDto).message : "";

  // Handle file click - preview for images/PDF, download for others
  const handleFileClick = () => {
    if (!attachment || isPending || isError) return;

    const fileExtension = attachment.fileName.split(".").pop()?.toLowerCase();
    const isPdf = fileExtension === "pdf";
    const canPreview = attachment.isImage || isPdf;

    if (canPreview) {
      setShowPreview(true);
    } else if (onDownloadFile) {
      onDownloadFile(attachment.url, attachment.fileName);
    }
  };

  // Skip empty messages
  if (!text?.trim() && !attachment && !isDeleted) {
    return null;
  }

  // Determine Bubble Variant
  const getBubbleVariant = () => {
    if (isDeleted) return "system";
    if (isError) return "error";
    if (sender === "me") return "me";
    return "other";
  };

  return (
    <>
      <MessageLayout
        sender={sender}
        partyName={partyName}
        time={time}
        isRead={isRead}
        isPending={isPending}
        isError={isError}
        isDeleted={isDeleted}
        showAvatar={showAvatar}
        showName={showName}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
        variant="text"
        onRetry={() => onRetry?.(id)}
      >
        <ChatBubble variant={getBubbleVariant()} className={borderRadiusClass}>
          {isDeleted ? (
            <div className="flex items-center gap-2 italic">
              <CornerUpLeft className="w-4 h-4 shrink-0" />
              <Typography variant="body-sm">회수된 메시지입니다</Typography>
            </div>
          ) : attachment ? (
            attachment.isImage && !isCmarketDocument ? (
              // Image Attachment
              <div
                className={cn(
                  "max-w-[180px] sm:max-w-[200px] cursor-pointer relative -m-2",
                  (isPending || isError) && "cursor-not-allowed opacity-80",
                )}
                onClick={handleFileClick}
              >
                <img
                  src={attachment.url}
                  alt={attachment.fileName}
                  onLoad={onImageLoad}
                  className="rounded-lg border border-border"
                />
                {isPending && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-lg backdrop-blur-[1px]">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
                {isError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-destructive/20 rounded-lg backdrop-blur-[1px]">
                    <AlertCircle className="w-6 h-6 text-destructive" />
                  </div>
                )}
              </div>
            ) : (
              // File Attachment
              <a
                href={isCmarketDocument ? attachment.url : undefined}
                download={isCmarketDocument ? attachment.fileName : undefined}
                target={isCmarketDocument ? "_blank" : undefined}
                rel={isCmarketDocument ? "noopener noreferrer" : undefined}
                onClick={isCmarketDocument ? undefined : handleFileClick}
                className={cn(
                  "flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-80 transition-opacity relative",
                  (isPending || isError) && "cursor-not-allowed opacity-70",
                )}
              >
                <div
                  className={cn(
                    "w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0",
                    sender === "me"
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  {isPending ? (
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                  ) : isError ? (
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" />
                  ) : (
                    <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Typography
                    variant="body-sm"
                    className="font-medium truncate block"
                    color={sender === "me" ? "white" : "default"}
                  >
                    {attachment.fileName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={sender === "me" ? "white" : "muted"}
                    className={sender === "me" ? "opacity-80" : ""}
                  >
                    {(attachment.fileSize / 1024).toFixed(1)} KB
                  </Typography>
                </div>
              </a>
            )
          ) : (
            <Typography
              variant="body-sm"
              className="leading-relaxed whitespace-pre-wrap break-all"
              color={sender === "me" ? "white" : "default"}
            >
              {text.split(/(https?:\/\/[^\s]+)/g).map((part, i) =>
                /^https?:\/\//.exec(part) ? (
                  <a
                    key={i}
                    href={part}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-current underline-offset-2 hover:opacity-80 transition-opacity"
                  >
                    {part}
                  </a>
                ) : (
                  part
                ),
              )}
            </Typography>
          )}
        </ChatBubble>

        {isError && (
          <Typography
            variant="caption"
            color="destructive"
            className="mt-1 flex items-center gap-1"
          >
            <AlertCircle className="w-3 h-3" />
            전송 실패
          </Typography>
        )}
      </MessageLayout>

      {showPreview && attachment && (
        <FilePreviewModal
          file={{
            url: attachment.url,
            fileName: attachment.fileName,
            fileSize: attachment.fileSize,
            isImage: attachment.isImage,
          }}
          onClose={() => setShowPreview(false)}
        />
      )}
    </>
  );
};
